/**
 * Task-Competencies Matrix Tab
 *
 * Read-only overview showing all tasks with their linked competencies.
 * Provides a quick reference for the task-competency mapping.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { tasksApi, type TaskListItem } from '../../../lib/tasksApi';
import { matchesSearch } from '../../../lib/textUtils';

type StatusFilter = 'all' | 'active' | 'inactive';

export function TaskCompetenciesTab() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch all tasks with their competencies
  const { data: tasksData, isLoading, error } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => tasksApi.list(),
  });

  // Filter tasks (accent-insensitive)
  const filteredTasks = tasksData?.data.filter(task => {
    const matchesStatusFilter =
      statusFilter === 'all' ||
      (statusFilter === 'active' && task.isActief) ||
      (statusFilter === 'inactive' && !task.isActief);
    const matchesSearchFilter =
      matchesSearch(task.name, searchQuery) ||
      task.competencies?.some(c => matchesSearch(c.name, searchQuery));
    return matchesStatusFilter && matchesSearchFilter;
  }) ?? [];

  // Count statistics
  const stats = {
    totalTasks: filteredTasks.length,
    tasksWithCompetencies: filteredTasks.filter(t => t.competencies && t.competencies.length > 0).length,
    tasksWithoutCompetencies: filteredTasks.filter(t => !t.competencies || t.competencies.length === 0).length,
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van taken.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Taken-Competenties Overzicht</h2>
        <p className="text-sm text-gray-500">
          Overzicht van alle taken met hun gekoppelde vakcompetenties
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-700">{stats.totalTasks}</div>
          <div className="text-sm text-blue-600">Taken totaal</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-700">{stats.tasksWithCompetencies}</div>
          <div className="text-sm text-green-600">Met competenties</div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-700">{stats.tasksWithoutCompetencies}</div>
          <div className="text-sm text-yellow-600">Zonder competenties</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label htmlFor="status-filter" className="sr-only">Status filter</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          >
            <option value="all">Alle taken</option>
            <option value="active">Actief</option>
            <option value="inactive">Inactief</option>
          </select>
        </div>
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">Zoeken</label>
          <input
            type="text"
            id="search"
            placeholder="Zoeken op taak of competentie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      </div>

      {/* Tasks List with Competencies */}
      {filteredTasks.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Taak
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Weging
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Competenties
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTasks.map((task: TaskListItem) => (
                <tr key={task.id} className={`hover:bg-gray-50 ${!task.isActief ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {task.id}
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{task.name}</div>
                      {task.beschrijving && (
                        <div className="text-xs text-gray-500 truncate max-w-md" title={task.beschrijving}>
                          {task.beschrijving}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {task.weging} pt
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {task.competencies && task.competencies.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {task.competencies.map((comp, index) => (
                          <span
                            key={comp.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                            title={`Competentie ${index + 1}`}
                          >
                            {comp.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Geen competenties</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      task.isActief ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {task.isActief ? 'Actief' : 'Inactief'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen taken gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">
            Pas de filters aan om taken te zien.
          </p>
        </div>
      )}

      {/* Info text */}
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-500">
          <strong>Tip:</strong> Gebruik de "Taken" tab om taken te bewerken en competenties te koppelen.
          Dit overzicht is alleen-lezen.
        </p>
      </div>
    </div>
  );
}
